"use client";

import clsx from "clsx";
import { useEffect, useState } from "react";

import { GenericSection } from "./ui";

interface IJourneyStep {
  title: string;
  description: string;
}

const steps: IJourneyStep[] = [
  {
    title: "Avaliação Inicial",
    description:
      "Nesta fase, realiza-se uma consulta para entender os objetivos, hábitos alimentares, histórico de saúde e preferências individuais.",
  },
  {
    title: "Elaboração do Plano Alimentar",
    description:
      "Com base na avaliação, desenvolve-se um plano alimentar personalizado, alinhado aos objetivos e necessidades específicas.",
  },
  {
    title: "Acompanhamento Contínuo",
    description:
      "São realizadas consultas periódicas para monitorar o progresso, ajustar o plano conforme necessário e fornecer suporte contínuo.",
  },
  {
    title: "Educação Nutricional",
    description:
      "Durante todo o processo, oferece-se orientação sobre escolhas alimentares saudáveis, promovendo autonomia e entendimento sobre nutrição.",
  },
  {
    title: "Manutenção dos Resultados",
    description:
      "Após alcançar os objetivos, trabalha-se na consolidação de hábitos saudáveis para garantir a manutenção dos resultados a longo prazo.",
  },
];

export const Journey = () => {
  const [activeStep, setActiveStep] = useState<number>(0);

  useEffect(() => {
    const handleScroll = () => {
      const steps = Array.from(document.querySelectorAll(".journey-step"));

      const lastVisibleIndex = steps.reduce((visibleIndex, element, index) => {
        const rect = element.getBoundingClientRect();
        const isVisible =
          rect.top < window.innerHeight * 0.75 &&
          rect.bottom > window.innerHeight * 0.2;
        const isAboveViewport = rect.bottom < window.innerHeight * 0.2;

        return isVisible ? index : isAboveViewport ? index - 1 : visibleIndex;
      }, 0);

      setActiveStep((prev) =>
        Math.max(0, lastVisibleIndex !== prev ? lastVisibleIndex : prev)
      );
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <GenericSection
      id="journey"
      heading="Processo"
      subheading="Etapas da Jornada"
      description="Entenda o caminho que iremos seguir para alcançar os seus objetivos."
      childrenClassname="w-full mb-3"
    >
      <div className="relative max-w-7xl mx-auto">
        <div className="hidden sm:block absolute top-0 left-1/2 transform -translate-x-1/2 w-1 h-full bg-primary-light">
          <div
            className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-primary transition-all duration-500"
            style={{ height: `${(activeStep / (steps.length - 1)) * 100}%` }}
          ></div>

          {steps.map((_, index) => (
            <div
              key={`step-${index}`}
              className={clsx(
                "absolute left-1/2 transform -translate-x-1/2 w-6 h-6 rounded-full transition-all duration-1000",
                index <= activeStep ? "bg-primary" : "bg-primary-light"
              )}
              style={{
                top: `${(index / (steps.length - 1)) * 100}%`,
              }}
            ></div>
          ))}
        </div>

        <div className="flex flex-col gap-12">
          {steps.map((step, index) => (
            <div
              key={`step-${index}`}
              className={`relative journey-step flex ${
                index % 2 === 0 ? "justify-end" : "justify-start"
              } w-full`}
            >
              <div
                className={`p-6 bg-white shadow-[0px_0px_4px_0px_#0000001a] rounded-lg ${
                  index % 2 === 0
                    ? "ml-auto sm:text-left"
                    : "mr-auto sm:text-right"
                } w-full sm:w-[45%]`}
                role="article"
                aria-labelledby={`step-${index}`}
              >
                <h3
                  id={`step-${index}`}
                  className="text-primary font-semibold mb-2"
                >
                  ETAPA {(index + 1).toString().padStart(2, "0")}
                </h3>
                <h4 className="text-heading font-bold text-lg mb-4">
                  {step.title}
                </h4>
                <p className="text-paragraph">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </GenericSection>
  );
};
